/**
 * Reader / annotation surface for a workspace writing.
 *
 * The right-hand panel in Writings.jsx mounts this when the admin is in
 * "Önizleme Modu". It behaves like a reader with annotation tools:
 *   - highlight the selection in 4 colours
 *   - underline / strike through the selection
 *   - attach a short margin note to the selection (hover to read)
 *   - zoom text in / out (preview-only, never saved to the body)
 *   - clear all annotations
 *   - share via email (opens a tiny inline composer)
 *
 * Annotations are stored on the server in `workspace_annotations`
 * (keyed by writing_id). The server copy of the writing is never
 * modified; only the annotated HTML snapshot in a side-table changes.
 */
import { useEffect, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  Underline as UnderlineIcon, Strikethrough, StickyNote,
  Eraser, ZoomIn, ZoomOut, Pencil, Share2, Save, X, Mail, Send, Trash2,
  BookOpen, Loader2,
} from "lucide-react";
import { api, errMsg } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";

const HILITE_COLORS = [
  { key: "yellow", label: "Sarı",   css: "#fef08a" },
  { key: "pink",   label: "Pembe",  css: "#fbcfe8" },
  { key: "green",  label: "Yeşil",  css: "#bbf7d0" },
  { key: "blue",   label: "Mavi",   css: "#bfdbfe" },
];

const FONT_SIZES = [14, 15, 16, 17, 18, 19, 20, 22];

export default function WritingPreview({ writing, onBack, onEdit }) {
  const qc = useQueryClient();
  const containerRef = useRef(null);
  // Persist the chosen reading size across sessions so the admin isn't forced
  // to re-zoom every time they open a writing.
  const [fontIdx, setFontIdx] = useState(() => {
    const v = typeof window !== "undefined" ? window.localStorage.getItem("wp-font-idx") : null;
    const n = v == null ? 2 : parseInt(v, 10);
    return Number.isFinite(n) && n >= 0 && n < FONT_SIZES.length ? n : 2;
  });
  useEffect(() => {
    try { window.localStorage.setItem("wp-font-idx", String(fontIdx)); } catch { /* quota */ }
  }, [fontIdx]);
  const [dirty, setDirty] = useState(false);
  const [initialHTML, setInitialHTML] = useState("");
  const [showSharePane, setShowSharePane] = useState(false);
  const [saving, setSaving] = useState(false);

  // Load any saved annotation HTML snapshot for this writing from the server.
  const { data: annotation, isLoading: annotLoading } = useQuery({
    queryKey: ["workspace", "writing-annot", writing?.id],
    queryFn: () =>
      api.get(`/admin/workspace/writings/${writing.id}/annotations`).then((r) => r.data),
    enabled: !!writing?.id,
    staleTime: 0,
  });

  // Decide which HTML to render whenever the writing or saved annotations
  // change: use the saved annotated version when it exists, otherwise the
  // raw body coming with the writing.
  useEffect(() => {
    if (!writing?.id) return;
    const savedHTML = (annotation?.html || "").trim();
    setInitialHTML(savedHTML || writing.body || "");
    setDirty(false);
    requestAnimationFrame(() => attachNoteHandlers());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [writing?.id, annotation?.html]);

  const attachNoteHandlers = () => {
    const el = containerRef.current;
    if (!el) return;
    el.querySelectorAll("[data-annot-note]").forEach((n) => {
      n.title = n.getAttribute("data-annot-note") || "";
    });
  };

  // --- Annotation helpers ------------------------------------------------
  const withSelection = (fn) => {
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0 || sel.isCollapsed) {
      toast.error("Önce metin seçin");
      return;
    }
    const el = containerRef.current;
    if (!el || !el.contains(sel.anchorNode)) {
      toast.error("Seçim yazının içinde değil");
      return;
    }
    fn(sel);
    setDirty(true);
    attachNoteHandlers();
    sel.removeAllRanges();
  };

  const wrapSelection = (styleProps) => withSelection((sel) => {
    const range = sel.getRangeAt(0);
    const span = document.createElement("span");
    Object.assign(span.style, styleProps);
    span.setAttribute("data-annot", "1");
    try {
      span.appendChild(range.extractContents());
      range.insertNode(span);
    } catch { /* nested nodes with disallowed structure; ignore */ }
  });

  const highlight = (color) =>
    wrapSelection({ backgroundColor: color.css, padding: "0 2px", borderRadius: "2px" });

  const underline = () =>
    wrapSelection({ textDecoration: "underline", textDecorationColor: "#dc2626",
                    textUnderlineOffset: "3px", textDecorationThickness: "2px" });

  const strike = () =>
    wrapSelection({ textDecoration: "line-through", color: "#78716c" });

  const addNote = () => withSelection((sel) => {
    const note = window.prompt("Kenar notu:");
    if (!note || !note.trim()) return;
    const range = sel.getRangeAt(0);
    const span = document.createElement("span");
    span.setAttribute("data-annot", "1");
    span.setAttribute("data-annot-note", note.trim());
    Object.assign(span.style, {
      backgroundColor: "#fef3c7",
      borderBottom: "2px dotted #d97706",
      padding: "0 2px",
      cursor: "help",
    });
    try {
      span.appendChild(range.extractContents());
      range.insertNode(span);
    } catch { /* ignore */ }
  });

  const clearAll = () => {
    if (!window.confirm("Tüm vurgu ve notlar kaldırılacak. Onaylıyor musunuz?")) return;
    const el = containerRef.current;
    if (!el) return;
    el.querySelectorAll('[data-annot="1"]').forEach((n) => {
      const parent = n.parentNode;
      while (n.firstChild) parent.insertBefore(n.firstChild, n);
      parent.removeChild(n);
    });
    setDirty(true);
    toast.success("Vurgular temizlendi");
  };

  // Persist the current annotated HTML to the server.
  const persist = async () => {
    const el = containerRef.current;
    if (!el || !writing?.id) return;
    setSaving(true);
    try {
      await api.put(`/admin/workspace/writings/${writing.id}/annotations`, {
        html: el.innerHTML,
      });
      setDirty(false);
      toast.success("Vurgular MongoDB'ye kaydedildi");
      qc.invalidateQueries({ queryKey: ["workspace", "writing-annot", writing.id] });
    } catch (e) { toast.error(errMsg(e)); }
    setSaving(false);
  };

  const discardSaved = async () => {
    if (!writing?.id) return;
    if (!window.confirm("Veritabanında kayıtlı vurgular silinecek. Onaylıyor musunuz?")) return;
    try {
      await api.delete(`/admin/workspace/writings/${writing.id}/annotations`);
      toast.success("Kayıtlı vurgular silindi");
      qc.invalidateQueries({ queryKey: ["workspace", "writing-annot", writing.id] });
    } catch (e) { toast.error(errMsg(e)); }
  };

  // When switching away with unsaved changes, offer one chance to save.
  const askSaveThenRun = async (next) => {
    if (!dirty) { next(); return; }
    const choice = window.confirm(
      "Kaydedilmemiş vurgularınız var. MongoDB'ye kaydedilsin mi?\n\n" +
      "Tamam → kaydet ve devam et\nİptal → kaydetmeden devam et",
    );
    if (choice) await persist();
    next();
  };

  // --- Share -------------------------------------------------------------
  const [shareTo, setShareTo] = useState("");
  const [shareMsg, setShareMsg] = useState("");
  const [sharing, setSharing] = useState(false);

  const doShare = async () => {
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(shareTo)) {
      toast.error("Geçerli bir e-posta girin");
      return;
    }
    setSharing(true);
    try {
      await api.post(`/admin/workspace/writings/${writing.id}/share`, {
        to: shareTo.trim(),
        message: shareMsg.trim(),
        include_cover: true,
      });
      toast.success(`"${writing.title || "Yazı"}" ${shareTo}'a gönderildi`);
      setShareTo(""); setShareMsg(""); setShowSharePane(false);
    } catch (e) { toast.error(errMsg(e)); }
    setSharing(false);
  };

  // Allow highlighting while blocking actual text edits inside the reader.
  const onBeforeInput = (e) => { e.preventDefault(); };

  const fontSize = FONT_SIZES[fontIdx];
  const hasSaved = !!(annotation?.html && annotation.html.trim());

  if (!writing) {
    return (
      <div className="h-full flex items-center justify-center text-stone-400 italic p-10 text-center">
        <div>
          <BookOpen size={32} className="mx-auto mb-2 opacity-40" />
          <p className="text-sm">Soldan bir yazı seçin</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full bg-white" data-testid="writing-preview">
      {/* Toolbar */}
      <div className="shrink-0 border-b border-stone-200 bg-gradient-to-r from-rose-50 to-amber-50 px-3 py-2 flex items-center flex-wrap gap-1.5">
        <Button
          size="sm" variant="ghost"
          onClick={() => askSaveThenRun(onBack)}
          className="text-stone-600"
          data-testid="wp-back"
        >
          <X size={14} className="mr-1" /> Kapat
        </Button>

        <div className="h-5 w-px bg-stone-300 mx-1" />

        {HILITE_COLORS.map((c) => (
          <button
            key={c.key}
            onClick={() => highlight(c)}
            title={`Vurgula — ${c.label}`}
            className="w-7 h-7 rounded-full border border-stone-300 hover:scale-110 transition shadow-sm"
            style={{ backgroundColor: c.css }}
            data-testid={`wp-hl-${c.key}`}
          />
        ))}

        <button onClick={underline} title="Altını çiz"
                className="p-1.5 rounded hover:bg-white border border-transparent hover:border-stone-200"
                data-testid="wp-underline">
          <UnderlineIcon size={15} className="text-red-700" />
        </button>
        <button onClick={strike} title="Üzeri çizili"
                className="p-1.5 rounded hover:bg-white border border-transparent hover:border-stone-200"
                data-testid="wp-strike">
          <Strikethrough size={15} className="text-stone-700" />
        </button>
        <button onClick={addNote} title="Kenar notu ekle"
                className="p-1.5 rounded hover:bg-white border border-transparent hover:border-stone-200"
                data-testid="wp-note">
          <StickyNote size={15} className="text-amber-700" />
        </button>
        <button onClick={clearAll} title="Vurguları temizle"
                className="p-1.5 rounded hover:bg-white border border-transparent hover:border-stone-200"
                data-testid="wp-clear">
          <Eraser size={15} className="text-stone-600" />
        </button>

        <div className="h-5 w-px bg-stone-300 mx-1" />

        <button onClick={() => setFontIdx((i) => Math.max(0, i - 1))}
                title="Küçült"
                disabled={fontIdx === 0}
                className="p-1.5 rounded hover:bg-white border border-transparent hover:border-stone-200 disabled:opacity-40"
                data-testid="wp-zoom-out">
          <ZoomOut size={15} />
        </button>
        <span className="text-xs tabular-nums text-stone-600 min-w-[32px] text-center">{fontSize}</span>
        <button onClick={() => setFontIdx((i) => Math.min(FONT_SIZES.length - 1, i + 1))}
                title="Büyüt"
                disabled={fontIdx === FONT_SIZES.length - 1}
                className="p-1.5 rounded hover:bg-white border border-transparent hover:border-stone-200 disabled:opacity-40"
                data-testid="wp-zoom-in">
          <ZoomIn size={15} />
        </button>

        <span className="flex-1" />

        {dirty && (
          <Button size="sm" variant="outline" onClick={persist} disabled={saving} data-testid="wp-save">
            {saving ? <Loader2 size={13} className="mr-1 animate-spin" /> : <Save size={13} className="mr-1" />}
            {saving ? "Kaydediliyor..." : "Vurguları kaydet"}
          </Button>
        )}
        {hasSaved && !dirty && (
          <Button size="sm" variant="ghost" onClick={discardSaved}
                  className="text-stone-500 hover:text-red-600" data-testid="wp-discard-saved">
            <Trash2 size={13} className="mr-1" /> Kayıtlı vurgular
          </Button>
        )}
        <Button size="sm" variant="outline"
                onClick={() => setShowSharePane((v) => !v)}
                data-testid="wp-share-toggle">
          <Share2 size={13} className="mr-1" /> Paylaş
        </Button>
        <Button size="sm" onClick={() => askSaveThenRun(() => onEdit(writing))} data-testid="wp-switch-edit">
          <Pencil size={13} className="mr-1" /> Düzenle
        </Button>
      </div>

      {/* Share pane */}
      {showSharePane && (
        <div className="shrink-0 border-b border-indigo-100 bg-indigo-50/60 px-3 py-3" data-testid="wp-share-pane">
          <div className="flex items-center gap-2 mb-2 text-xs font-semibold text-indigo-800">
            <Mail size={13} /> E-posta ile paylaş
          </div>
          <div className="grid sm:grid-cols-[1fr,auto] gap-2 mb-2">
            <Input
              type="email"
              placeholder="alici@eposta.com"
              value={shareTo}
              onChange={(e) => setShareTo(e.target.value)}
              className="h-9 text-sm"
              data-testid="wp-share-email"
            />
            <Button size="sm" onClick={doShare} disabled={sharing} data-testid="wp-share-send">
              <Send size={13} className="mr-1" /> {sharing ? "Gönderiliyor..." : "Gönder"}
            </Button>
          </div>
          <Textarea
            placeholder="Opsiyonel kişisel not (örn. 'Şu paragrafı sana özel yazdım.')"
            value={shareMsg}
            onChange={(e) => setShareMsg(e.target.value)}
            rows={2}
            className="text-xs"
            data-testid="wp-share-message"
          />
          <p className="text-[11px] text-stone-500 mt-1.5">
            Yazının kapak görseli, başlığı ve içeriği profesyonel bir e-posta tasarımında gönderilir.
          </p>
        </div>
      )}

      {/* Reader body */}
      <div className="flex-1 overflow-y-auto bg-gradient-to-b from-stone-50 to-white"
           onBeforeInput={onBeforeInput}
           data-testid="wp-scroll">
        <article className="max-w-3xl mx-auto px-4 sm:px-8 lg:px-12 py-8">
          {writing.cover_url && (
            <img src={writing.cover_url} alt=""
                 className="w-full max-h-80 object-cover rounded-xl mb-6 shadow-md" />
          )}
          <div className="text-[11px] uppercase tracking-widest text-rose-700 font-semibold mb-2">
            {writing.category || "Yazı"}
            <span className="mx-2 text-stone-300">·</span>
            <span className="text-stone-500 normal-case tracking-normal">
              {writing.word_count || 0} kelime
            </span>
            {hasSaved && (
              <>
                <span className="mx-2 text-stone-300">·</span>
                <span className="text-emerald-600 normal-case tracking-normal">
                  ✓ MongoDB'de kayıtlı vurgular var
                </span>
              </>
            )}
          </div>
          <h1 className="font-serif text-3xl sm:text-4xl font-bold text-slate-900 leading-tight mb-6"
              data-testid="wp-title">
            {writing.title || "(başlıksız)"}
          </h1>
          {annotLoading ? (
            <div className="text-center py-10 text-stone-400 text-sm">
              <Loader2 size={20} className="animate-spin mx-auto mb-2" />
              Vurgular yükleniyor...
            </div>
          ) : (
            <div
              ref={containerRef}
              contentEditable
              suppressContentEditableWarning
              spellCheck={false}
              className="rich-content max-w-none outline-none focus:ring-2 focus:ring-amber-200 rounded"
              style={{ fontSize: `${fontSize}px`, lineHeight: 1.75 }}
              dangerouslySetInnerHTML={{ __html: initialHTML || "<p class='text-stone-400 italic'>Bu yazı boş.</p>" }}
              data-testid="wp-body"
            />
          )}
        </article>
      </div>
    </div>
  );
}
